import { useEffect, useRef, type RefObject } from "react";

import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import { timelineGpuSceneFromLayout } from "@/lib/timeline/board/ChapterBoard/gpuScene";
import { timelineRailAxisY } from "@/lib/timeline/marker/MarkerMetrics/metrics";
import {
  canStartTimelinePanDrag,
  type TimelineViewTransform,
} from "@/lib/timeline/view/ViewTransform/transform";
import { tickNear } from "@/lib/timeline-gpu/scene-mark-hit";

/** How close to a tick a click still lands on it, in screen pixels. */
const REACH_PX = 24;
/** Further than this between press and release is a drag, not a click. */
const DRAG_PX = 6;

/** Clicking on, or right beside, a tick on a yarn moves the playhead to that scene. */
export function useTickClickSeek(
  { stageRef, getViewTransform, getViewportWidth }: {
    stageRef: RefObject<HTMLElement | null>;
    getViewTransform: () => TimelineViewTransform;
    getViewportWidth: () => number;
  },
  layout: BookTimelineLayout,
  seekTo: (playheadMs: number) => void,
): void {
  const latest = useRef({ layout, getViewTransform, getViewportWidth, seekTo });
  latest.current = { layout, getViewTransform, getViewportWidth, seekTo };

  useEffect(() => {
    const stage = stageRef.current;
    if (stage === null) {
      return;
    }
    let press: { x: number; y: number } | null = null;
    const onDown = (event: PointerEvent): void => {
      press = canStartTimelinePanDrag(event.target)
        ? { x: event.clientX, y: event.clientY }
        : null;
    };
    const onUp = (event: PointerEvent): void => {
      const start = press;
      press = null;
      const moved = start === null
        ? Infinity
        : Math.hypot(event.clientX - start.x, event.clientY - start.y);
      if (start === null || moved > DRAG_PX) {
        return;
      }
      const now = latest.current;
      const view = now.getViewTransform();
      const rect = stage.getBoundingClientRect();
      const point = {
        x: (event.clientX - rect.left - view.panX) / view.zoom,
        y: (event.clientY - rect.top - view.panY) / view.zoom,
      };
      const scene = timelineGpuSceneFromLayout(now.layout);
      const startMs = tickNear(
        scene.sceneStarts,
        scene.branches,
        timelineRailAxisY(),
        now.getViewportWidth(),
        point,
        REACH_PX / view.zoom,
      );
      if (startMs !== undefined) {
        now.seekTo(startMs);
      }
    };
    stage.addEventListener("pointerdown", onDown);
    stage.addEventListener("pointerup", onUp);
    return () => {
      stage.removeEventListener("pointerdown", onDown);
      stage.removeEventListener("pointerup", onUp);
    };
  }, [stageRef]);
}
